import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Stack, Text } from '@mantine/core';
import InboxSkeletonLoader from '@components/InboxReviews/InboxSkeletonLoader';
import TodoNotificationsTable from '@components/InboxReviews/Tables/TodoNotificationsTable';
import ThPagination from '@components/ThPagination/ThPagination';
import { useWorkerTodos } from '@worker/hooks/useWorkerTodos';
import { useWorkerInboxQueryState } from '@worker/hooks/useWorkerInboxQueryState';
import { useRecommendationActions } from '@worker/hooks/useRecommendationActions';
import { useApplyFlow } from '@worker/hooks/useApplyFlow';
import { TodoType } from '@th-types/inbox-reviews.type';
import type { TodoItem } from '@th-types/inbox-reviews.type';
import { JobMutationAction } from '@worker/types/myJobs.type';
import { handleInboxPageChange } from '@utils/InboxHelper';
import { DEFAULT_ITEMS_PER_PAGE } from '@utils/Pagination';
import { BASE_PATH } from '@utils/Environment';
import { buildJobViewUrl } from '@worker/helpers/my-jobs-helpers';
import type { TransformedJobItem } from '@worker/types/myJobs.type';
import JobDetailsModal from '@worker/components/MyJobs/JobDetailsModal/JobDetailsModal';
import {
  JobFarAwayModal,
  ProfileIncompleteModal,
  JobQuestionsModal,
} from '@worker/components/MyJobs/modals';
import { useOpenRateCompanyModal } from '@worker/stores/workerStore';

function goToLegacyJobDetail(jobAssignmentId: number) {
  const url = buildJobViewUrl(jobAssignmentId, null);
  if (url) window.location.href = url;
}

export default function TodoTab() {
  const { searchParams, setSearchParams, urlParams } =
    useWorkerInboxQueryState();
  const navigate = useNavigate();
  const currentOffset = urlParams.offset ?? 0;

  const [selectedRecommendationId, setSelectedRecommendationId] = useState<
    number | null
  >(null);

  const recommendationActions = useRecommendationActions();
  const applyFlow = useApplyFlow();
  const openRateCompanyModal = useOpenRateCompanyModal();

  const openRecommendationModal = (todo: TodoItem) => {
    if (todo.jobRecommendationId == null) return;
    setSelectedRecommendationId(todo.jobRecommendationId);
  };

  const handleCloseModal = () => {
    setSelectedRecommendationId(null);
  };

  const handleApplyFromModal = (recommendation: TransformedJobItem) => {
    setSelectedRecommendationId(null);
    applyFlow.startApply(recommendation);
  };

  const handleOpenTodo = (todo: TodoItem) => {
    if (
      todo.type === TodoType.ENTER_RATING ||
      todo.type === TodoType.ENTER_RATING_PAYMENT
    ) {
      if (todo.jobAssignmentId != null) {
        openRateCompanyModal(todo.jobAssignmentId);
      }
      return;
    }

    if (todo.type === TodoType.RECOMMENDED_JOB) {
      if (todo.jobAssignmentId != null) {
        goToLegacyJobDetail(todo.jobAssignmentId);
      } else {
        openRecommendationModal(todo);
      }
      return;
    }

    if (todo.jobAssignmentId == null) {
      navigate(`${BASE_PATH}jobs/my-jobs/new`);
      return;
    }

    goToLegacyJobDetail(todo.jobAssignmentId);
  };

  const getEventOnClick = (todo: TodoItem) =>
    todo.type === TodoType.RECOMMENDED_JOB &&
    todo.jobAssignmentId == null &&
    todo.jobRecommendationId != null
      ? () => openRecommendationModal(todo)
      : undefined;

  const getCompanyHref = (todo: TodoItem) =>
    todo.profileURLName
      ? `${import.meta.env.VITE_SERVER_BASE_URL}/companies/${todo.profileURLName}`
      : undefined;

  const getEventHref = (todo: TodoItem) =>
    todo.jobAssignmentId != null
      ? (buildJobViewUrl(todo.jobAssignmentId, null) ?? undefined)
      : undefined;

  const handleDismissTodo = (todo: TodoItem) => {
    if (todo.jobRecommendationId == null) return;
    recommendationActions.handleDismissById(todo.jobRecommendationId);
  };

  const dismissingId =
    recommendationActions.mutationState.action === JobMutationAction.DISMISS
      ? recommendationActions.mutationState.targetId
      : null;

  const handlePageChange = (offset: number) =>
    handleInboxPageChange(offset, searchParams, setSearchParams);

  // Todos endpoint is not paginated server-side: it returns up to `max` rows
  // and `count` is the size of that slice, not a global total. We fetch with
  // the hook's default (max=100) — same params as the parent's badge query so
  // React Query dedupes them — and slice locally for the visible page.
  const { data: todosData, isLoading } = useWorkerTodos();

  const todos = useMemo<TodoItem[]>(
    () => todosData?.list ?? [],
    [todosData?.list]
  );
  const totalTodos = todos.length;
  const pageTodos = useMemo(
    () => todos.slice(currentOffset, currentOffset + DEFAULT_ITEMS_PER_PAGE),
    [todos, currentOffset]
  );

  useEffect(() => {
    if (isLoading) return;
    if (currentOffset > 0 && currentOffset >= totalTodos) {
      handleInboxPageChange(0, searchParams, setSearchParams);
    }
  }, [isLoading, currentOffset, totalTodos, searchParams, setSearchParams]);

  return (
    <Stack gap="md" w="100%" p={{ base: 0, sm: 'md' }}>
      {isLoading && <InboxSkeletonLoader />}

      {!isLoading && totalTodos === 0 && (
        <Text c="dimmed" ta="center" py="xl">
          No pending to-dos.
        </Text>
      )}

      {!isLoading && totalTodos > 0 && (
        <>
          <TodoNotificationsTable
            todos={pageTodos}
            onOpenTodo={handleOpenTodo}
            getEventOnClick={getEventOnClick}
            getCompanyHref={getCompanyHref}
            getEventHref={getEventHref}
            onDismiss={handleDismissTodo}
            dismissingId={dismissingId}
          />
          {totalTodos > DEFAULT_ITEMS_PER_PAGE && (
            <ThPagination
              currentOffset={currentOffset}
              itemsPerPage={DEFAULT_ITEMS_PER_PAGE}
              totalItems={totalTodos}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}

      <JobDetailsModal
        opened={selectedRecommendationId != null}
        onClose={handleCloseModal}
        jobRecommendationId={selectedRecommendationId}
        onDismiss={recommendationActions.handleDismiss}
        onBookmark={recommendationActions.handleBookmark}
        onShare={recommendationActions.handleShare}
        onMessage={recommendationActions.handleMessage}
        onApply={handleApplyFromModal}
        mutation={recommendationActions.mutationState}
      />

      <JobFarAwayModal {...applyFlow.modals.distance} />
      <ProfileIncompleteModal {...applyFlow.modals.profile} />
      <JobQuestionsModal {...applyFlow.modals.questions} />
    </Stack>
  );
}
